import dayjs from "dayjs";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import { redirectToAuth } from "@/pages/profile/helper/redirectToAuth";
import { StudentCertifyDrawer } from "../../components/student-certify-drawer";
import { UserChangeLogDrawer } from "../../components/user-change-log";
import { useModalFormStore } from "../../store/modal-form-store";
import { useTranslation } from "react-i18next";
import { ChangeCompanyModal } from "@/pages/profile/components/action-modal/change-company-modal";
import { ConfirmModal } from "@/components/renderbus-modal/confirm-modal";
import { useChangeCompany } from "@/pages/profile/hooks/useChangeCompany";
import { isEn } from "project-config/utils/env";
import { ArrowCard } from "./components/arrow-card";
import { InfoCard } from "./components/info-card";
import {
  CertifyTitle,
  CertifyCardWrapper,
  ChangeBtnGroup,
  RedPoint,
} from "./atom";
import { Space } from "task-eva-ui";
import { AuthType } from "./constants/auth-type";
import { CertifyStatus } from "./constants/certify-status";
import { replaceNameWithAsterisk, maskNumber } from "project-config/utils";
import { UserInfo } from "@/utils/userinfo/types";
import { IStudentAuthenticationInfo } from "../../types/certify";
import { IsReadChange } from "./constants/change-type";
import { useToastStore } from "@/store/toast-store";
import { useGlobalStore } from "@/store/GlobalStore";
import { useIsReadChange } from "../../hooks/useIsReadChange";
import { useEffect, useState } from "react";
import { Button } from "@renderbus/ui";
import { useLocation } from "react-router-dom";
import { useCertifyStore } from "../../store/certify-store";

export function CertifyCard({
  userInfo,
  isCertified = false,
  isDomestic = true,
  studentData,
  loading = true,
  certifyStatus = CertifyStatus.NoCertified,
  isPictureUser = false,
  isSubAccount = false,
}: {
  userInfo?: UserInfo;
  isCertified?: boolean;
  isDomestic?: boolean;
  loading?: boolean;
  studentData?: IStudentAuthenticationInfo;
  certifyStatus?: CertifyStatus;
  isPictureUser?: boolean;
  isSubAccount?: boolean;
}) {
  const t = getTranslateNsT("profile");
  const { t: useCenterT } = useTranslation("center");
  const Educatoins = [
    useCenterT("student_mod_identify_start_item_education_levels_0"),
    useCenterT("student_mod_identify_start_item_education_levels_1"),
    useCenterT("student_mod_identify_start_item_education_levels_2"),
    useCenterT("student_mod_identify_start_item_education_levels_3"),
  ];
  const { showGlobalToast } = useToastStore();
  const { studentCertifyInfo, isStudentValid } = useGlobalStore();
  const { isReadChange, isReadChangeRefetch } = useIsReadChange();
  const { updateCertifyState } = useCertifyStore();
  const location = useLocation();
  const [loadingStudent, setLoadingStudent] = useState(true);
  const {
    setOpenStudentCertifyDrawer,
    openUserChangeLogDrawer,
    setOpenUserChangeLogDrawer,
    openStudentVerifyConfirmModal,
    setOpenStudentVerifyConfirmModal,
    openStudentToEnterpriseModal,
    setOpenStudentToEnterpriseModal,
    openStudentTipsModal,
    setOpenStudentTipsModal,
  } = useModalFormStore();
  const {
    openCompanyModal,
    setOpenCompanyModal,
    changeCompanyForm,
    changeCompanyLoading,
    submitChangeCompany,
    clearChangeCompanyForm,
  } = useChangeCompany();

  const NoCertifiedList = [
    CertifyStatus.NoCertified,
    CertifyStatus.PersonFailed,
    CertifyStatus.EnterpriseFailed,
    CertifyStatus.PersonPending,
    CertifyStatus.EnterprisePending,
  ];

  const havePersonalCertification = [
    CertifyStatus.PersonPassed,
    CertifyStatus.StudentPending,
    CertifyStatus.StudentFail,
    CertifyStatus.PersonChangeEnterprise,
    CertifyStatus.StudentPassed,
    CertifyStatus.StudentExpire,
    CertifyStatus.PersonaChangeFailed,
    CertifyStatus.PersonalToEnterpriseFailed,
    CertifyStatus.PersonalChanging,
  ];

  const showStudentCardList = [
    CertifyStatus.StudentPassed,
    CertifyStatus.StudentExpire,
    CertifyStatus.PersonChangeEnterprise,
    CertifyStatus.PersonaChangeFailed,
    CertifyStatus.PersonalToEnterpriseFailed,
    CertifyStatus.PersonalChanging,
  ];

  useEffect(() => {
    if (!openUserChangeLogDrawer) {
      isReadChangeRefetch();
    }
  }, [isReadChangeRefetch, openUserChangeLogDrawer]);

  const personalOrEnterpriseCertificate = (
    isEnterprise?: boolean,
    type?: number
  ) => {
    if (
      [
        CertifyStatus.PersonPending,
        CertifyStatus.EnterprisePending,
        CertifyStatus.StudentPending,
        CertifyStatus.PersonChangeEnterprise,
        CertifyStatus.PersonalChanging,
      ].includes(certifyStatus)
    ) {
      showGlobalToast({
        type: "error",
        content: t("certify_reviewing_error_tip"),
      });
    } else {
      if (
        !studentCertifyInfo?.noStudentVerify &&
        isStudentValid &&
        isEnterprise
      ) {
        setOpenStudentToEnterpriseModal(true);
      } else if (type === AuthType.Enterprise) {
        redirectToAuth(
          userInfo,
          false,
          "/authenticate/enterprise-authenticate"
        );
      } else {
        redirectToAuth(userInfo);
      }
    }
  };

  const enterpriseChangeCertificate = () => {
    if (
      ![
        CertifyStatus.PersonChangeEnterprise,
        CertifyStatus.EnterpriseChange,
        CertifyStatus.PersonChangeEnterprise,
      ].includes(certifyStatus)
    ) {
      isDomestic ? redirectToAuth(userInfo) : setOpenCompanyModal(true);
    } else {
      showGlobalToast({
        type: "error",
        content: t("certify_reviewing_error_tip"),
      });
    }
  };

  const unCertifiedStudent = () => {
    setOpenStudentTipsModal(true);
  };

  const studentCertificate = () => {
    if (
      [
        CertifyStatus.PersonPending,
        CertifyStatus.EnterprisePending,
        CertifyStatus.StudentPending,
        CertifyStatus.PersonChangeEnterprise,
        CertifyStatus.PersonalChanging,
      ].includes(certifyStatus)
    ) {
      showGlobalToast({
        type: "error",
        content: t("certify_reviewing_error_tip"),
      });
    } else {
      if (!userInfo?.tempMemberValidityTime) {
        setOpenStudentCertifyDrawer(true);
      }
      if (userInfo?.tempMemberValidityTime) {
        setOpenStudentVerifyConfirmModal(true);
      }
    }
  };

  useEffect(() => {
    setLoadingStudent(true);
    updateCertifyState();
    setLoadingStudent(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, studentCertifyInfo, isDomestic]);

  useEffect(() => {
    if (!loadingStudent) {
      if (location.search.includes("education=true")) {
        // 自动点击学生认证行为
        if (NoCertifiedList.includes(certifyStatus) && !isPictureUser) {
          unCertifiedStudent();
          return;
        }
        if (
          havePersonalCertification.includes(certifyStatus) &&
          !isPictureUser &&
          !(
            showStudentCardList.includes(certifyStatus) &&
            studentCertifyInfo?.noStudentVerify !== undefined &&
            !studentCertifyInfo?.noStudentVerify &&
            studentCertifyInfo?.studentVerifyFailed !== undefined &&
            !studentCertifyInfo?.studentVerifyFailed &&
            studentCertifyInfo.studentVerifyPending !== undefined &&
            !studentCertifyInfo.studentVerifyPending
          )
        ) {
          studentCertificate();
          return;
        }
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingStudent, location.search]);

  return (
    <CertifyCardWrapper showTitle={!isCertified && isDomestic}>
      {!loading && (
        <Space column gap={10} fitParent>
          <Space gap={10} justify={"space-between"} fitParent>
            {isDomestic && (
              <>
                <CertifyTitle>{t("certify")}</CertifyTitle>
                <ChangeBtnGroup>
                  <span onClick={() => setOpenUserChangeLogDrawer(true)}>
                    {t("user_change_log")}
                    {isReadChange === IsReadChange.UnRead && <RedPoint />}
                  </span>
                  <a
                    href="https://www.renderbus.com/support-new/xrxz/smrz/smrz-faq/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("user_change_question")}
                  </a>
                </ChangeBtnGroup>
              </>
            )}
            {CertifyStatus.EnterpriseFox === certifyStatus && (
              <CertifyTitle>{t("certification_fox_title")}</CertifyTitle>
            )}
          </Space>
          {/* 未认证 */}
          {NoCertifiedList.includes(certifyStatus) && (
            <Space
              column
              gap={10}
              fitParent
              className="certified-person-wrapper"
            >
              <ArrowCard
                cardStyleName="uncertified-card"
                size="uncertified-large"
                handler={() => personalOrEnterpriseCertificate()}
                certifyStatus={certifyStatus}
              />
              <ArrowCard
                cardStyleName="uncertified-card"
                size="uncertified-large"
                type={AuthType.Enterprise}
                handler={() =>
                  personalOrEnterpriseCertificate(false, AuthType.Enterprise)
                }
                certifyStatus={certifyStatus}
              />
              {!isPictureUser && (
                <ArrowCard
                  size="uncertified-large"
                  type={AuthType.Student}
                  certifyStatus={certifyStatus}
                  handler={unCertifiedStudent}
                />
              )}
            </Space>
          )}
          {/* 已个人认证 + 未学生认证 or 已个人认证 + 已学生认证 */}
          {havePersonalCertification.includes(certifyStatus) && (
            <Space
              column
              gap={10}
              fitParent
              className="certified-person-wrapper"
            >
              <InfoCard
                info={{
                  realName:
                    replaceNameWithAsterisk(userInfo?.realName || "") || "",
                  certNumber: maskNumber(userInfo?.certNumber || "") || "",
                  authSuccessDate: userInfo?.authSuccessDate
                    ? dayjs(userInfo?.authSuccessDate).format("YYYY-MM-DD")
                    : "",
                }}
                certifyStatus={certifyStatus}
                handler={() => personalOrEnterpriseCertificate()}
              />
              {!isPictureUser &&
                (showStudentCardList.includes(certifyStatus) &&
                studentCertifyInfo?.noStudentVerify !== undefined &&
                !studentCertifyInfo?.noStudentVerify &&
                studentCertifyInfo?.studentVerifyFailed !== undefined &&
                !studentCertifyInfo?.studentVerifyFailed &&
                studentCertifyInfo.studentVerifyPending !== undefined &&
                !studentCertifyInfo.studentVerifyPending ? (
                  <InfoCard
                    disabled
                    type={AuthType.Student}
                    info={{
                      realName: studentData?.realName,
                      schoolAddress: studentData?.schoolAddress,
                      schoolName: studentData?.schoolName,
                      timeAge: studentData?.timeAge,
                      educational: studentData?.educational
                        ? Educatoins[studentData?.educational - 1]
                        : "",
                      startTime: studentData?.startTime
                        ? dayjs(studentData?.startTime).format("YYYY-MM-DD")
                        : "",
                      endTime: studentData?.endTime
                        ? dayjs(studentData?.endTime).format("YYYY-MM-DD")
                        : "",
                    }}
                    certifyStatus={certifyStatus}
                  />
                ) : (
                  <ArrowCard
                    size="uncertified-large"
                    type={AuthType.Student}
                    certifyStatus={certifyStatus}
                    handler={studentCertificate}
                  />
                ))}
              <ArrowCard
                size="uncertified-large"
                type={AuthType.Enterprise}
                certifyStatus={certifyStatus}
                authStatus={userInfo?.authStatus}
                handler={() =>
                  personalOrEnterpriseCertificate(true, AuthType.Enterprise)
                }
              />
            </Space>
          )}
          {/* 已企业认证*/}
          {[
            CertifyStatus.EnterprisePassed,
            CertifyStatus.EnterpriseChange,
            CertifyStatus.EnterpriseChangeFailed,
          ].includes(certifyStatus) && (
            <Space
              column
              gap={20}
              fitParent
              className="certified-company-wrapper"
            >
              <InfoCard
                type={
                  certifyStatus === CertifyStatus.PersonChangeEnterprise
                    ? AuthType.Person
                    : AuthType.Enterprise
                }
                info={
                  certifyStatus === CertifyStatus.PersonChangeEnterprise
                    ? {
                        realName:
                          replaceNameWithAsterisk(userInfo?.realName || "") ||
                          "",
                        certNumber:
                          maskNumber(userInfo?.certNumber || "") || "",
                        authSuccessDate: userInfo?.authSuccessDate
                          ? dayjs(userInfo?.authSuccessDate).format(
                              "YYYY-MM-DD"
                            )
                          : "",
                      }
                    : {
                        company: userInfo?.company || "-",
                        companySite: userInfo?.companySite || "-",
                      }
                }
                handler={enterpriseChangeCertificate}
                certifyStatus={certifyStatus}
                isSubAccount={isSubAccount}
              />
            </Space>
          )}
          {/* 海外企业信息展示 */}
          {CertifyStatus.EnterpriseFox === certifyStatus && (
            <Space
              column
              gap={20}
              fitParent
              className="certified-company-wrapper"
            >
              <InfoCard
                type={AuthType.FoxInvoiceInfo}
                info={{
                  company: userInfo?.company,
                  companySite: userInfo?.companySite,
                  vatNumber: userInfo?.vatNumber,
                }}
                handler={enterpriseChangeCertificate}
                certifyStatus={certifyStatus}
                isSubAccount={isSubAccount}
              />
            </Space>
          )}
        </Space>
      )}
      <ConfirmModal
        modalProps={{
          title: useCenterT("common_store_global_dialog_default_title"),
          open: openStudentVerifyConfirmModal,
          onOk: () => {
            setOpenStudentVerifyConfirmModal(false);
            setOpenStudentCertifyDrawer(true);
          },
          onCancel: () => setOpenStudentVerifyConfirmModal(false),
        }}
        translationNamespace="center"
        translationKey="student_verify_confirm_tip"
      />
      <ConfirmModal
        modalProps={{
          title: useCenterT("common_store_global_dialog_default_title"),
          open: openStudentToEnterpriseModal,
          onOk: () => {
            setOpenStudentToEnterpriseModal(false);
            redirectToAuth(userInfo);
          },
          onCancel: () => setOpenStudentToEnterpriseModal(false),
        }}
        translationNamespace="profile"
        translationKey="student_to_enterprise_tip"
      />
      <StudentCertifyDrawer />
      <UserChangeLogDrawer />
      <ChangeCompanyModal
        modalProps={{
          title: t("profile_section_action_change_company"),
          open: openCompanyModal,
          confirmLoading: changeCompanyLoading,
          onOk: () => submitChangeCompany(),
          onCancel: () => clearChangeCompanyForm(),
        }}
        formProps={{
          size: "large",
          labelCol: { span: isEn() ? 7 : 5 },
          colon: false,
          form: changeCompanyForm,
          requiredMark: false,
        }}
      />
      <ConfirmModal
        modalProps={{
          title: useCenterT("common_store_global_dialog_default_title"),
          open: openStudentTipsModal,
          width: 400,
          onCancel: () => setOpenStudentTipsModal(false),
          footer: (
            <Button
              type="primary"
              onClick={() => {
                redirectToAuth(userInfo);
                setOpenStudentTipsModal(false);
              }}
            >
              {t("certify_student_error_tip_link")}
            </Button>
          ),
        }}
        translationNamespace="profile"
        translationKey="student_warning_tip"
      />
    </CertifyCardWrapper>
  );
}
